import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icons";
import { useScrollLock } from "./Sheet";
import "./controls.css";

// The store's own form controls. Checkbox, Switch and RadioList keep a real
// <input> underneath (transparent, sitting on the drawn box), so "required",
// autofill-free forms and screen readers behave exactly as with native ones.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export function Checkbox({ checked, onChange, children, count, disabled, className, ...input }) {
  return (
    <label className={cx("sh-cb", disabled && "is-disabled", className)}>
      <input type="checkbox" className="sh-ctl-input" checked={!!checked} onChange={onChange} disabled={disabled} {...input} />
      <span className="sh-cb-box" aria-hidden="true">
        <Icon name="check" size={14} />
      </span>
      {children != null && <span className="sh-cb-text">{children}</span>}
      {count != null && <em className="sh-ctl-count">{count}</em>}
    </label>
  );
}

export function Switch({ checked, onChange, children, count, disabled, className, ...input }) {
  return (
    <label className={cx("sh-sw", disabled && "is-disabled", className)}>
      <span className="sh-sw-text">{children}</span>
      {count != null && <em className="sh-ctl-count">{count}</em>}
      <input type="checkbox" role="switch" className="sh-ctl-input" checked={!!checked} onChange={onChange} disabled={disabled} {...input} />
      <span className="sh-sw-track" aria-hidden="true">
        <span className="sh-sw-knob" />
      </span>
    </label>
  );
}

// One-of-many as a list of rows; arrow keys move between them natively.
export function RadioList({ name, value, onChange, options, className, ...group }) {
  const uid = useId();
  return (
    <div role="radiogroup" className={cx("sh-radios", className)} {...group}>
      {options.map((o) => (
        <label key={o.value} className={cx("sh-radio", o.value === value && "on")}>
          <input type="radio" className="sh-ctl-input" name={name || uid} value={o.value} checked={o.value === value} onChange={() => onChange(o.value)} />
          <span className="sh-radio-mark" aria-hidden="true" />
          <span className="sh-radio-text">{o.label}</span>
          {o.hint != null && <em className="sh-ctl-count">{o.hint}</em>}
        </label>
      ))}
    </div>
  );
}

// ---- select -----------------------------------------------------------------

const PHONE = "(max-width: 719px)";
const fold = (s) =>
  String(s)
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
const textOf = (o) => o.text ?? (typeof o.label === "string" || typeof o.label === "number" ? String(o.label) : String(o.value));

function useMedia(query) {
  const [on, setOn] = useState(() => !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const sync = () => setOn(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [query]);
  return on;
}

// A listbox in place of <select>. The list renders into <body> so drawers and
// sticky bars can't clip it; on phones the "field" kind opens as a bottom
// sheet, which is easier to work with a thumb than a small popover.
export function Select({ value, onChange, options, variant = "field", label, labelledBy, lead, placeholder = "Choose", align = "start", disabled, className, id }) {
  const uid = useId();
  const btnId = id || `${uid}b`;
  const listId = `${uid}l`;
  const labelId = labelledBy || (label ? `${uid}t` : null);
  const optId = (i) => `${uid}o${i}`;
  const btn = useRef(null);
  const list = useRef(null);
  const pop = useRef(null);
  const typed = useRef({ text: "", at: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null);
  const asSheet = useMedia(PHONE) && variant === "field";
  useScrollLock(open && asSheet);

  const selIndex = options.findIndex((o) => o.value === value);
  const sel = options[selIndex];
  const usable = (i) => !!options[i] && !options[i].disabled;
  const move = (from, by) => {
    const dir = by > 0 ? 1 : -1;
    let hit = from;
    for (let i = from + dir, left = Math.abs(by); i >= 0 && i < options.length && left > 0; i += dir) {
      if (usable(i)) {
        hit = i;
        left -= 1;
      }
    }
    return hit;
  };
  const first = () => move(-1, 1);
  const last = () => {
    const i = move(options.length, -1);
    return i < options.length ? i : -1;
  };

  const show = (at) => {
    if (disabled || !options.length) return;
    setActive(at ?? (selIndex >= 0 ? selIndex : first()));
    setPos(null);
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) btn.current?.focus({ preventScroll: true });
  };
  const pick = (i) => {
    if (!usable(i)) return;
    close();
    if (options[i].value !== value) onChange(options[i].value);
  };

  // Type-ahead: letters typed in quick succession narrow the match; the same
  // letter again steps through everything starting with it.
  const seek = (key) => {
    const t = typed.current;
    const now = Date.now();
    t.text = now - t.at > 700 ? key : t.text + key;
    t.at = now;
    const q = fold(t.text);
    const cycling = [...q].every((c) => c === q[0]);
    const needle = cycling ? q[0] : q;
    const from = Math.max(open ? active : selIndex, 0);
    const skip = cycling ? 1 : 0;
    for (let k = 0; k < options.length; k += 1) {
      const i = (from + skip + k) % options.length;
      if (usable(i) && fold(textOf(options[i])).startsWith(needle)) return i;
    }
    return -1;
  };
  const printable = (e) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const onTriggerKey = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      show(e.key === "ArrowUp" && selIndex < 0 ? last() : undefined);
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      show(e.key === "Home" ? first() : last());
    } else if (printable(e) && e.key !== " ") {
      const i = seek(e.key);
      if (i >= 0) show(i);
    }
  };

  const onListKey = (e) => {
    const keys = {
      ArrowDown: () => move(active, 1),
      ArrowUp: () => move(active, -1),
      PageDown: () => move(active, 8),
      PageUp: () => move(active, -8),
      Home: first,
      End: last,
    };
    if (keys[e.key]) {
      e.preventDefault();
      setActive(keys[e.key]());
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(active);
    } else if (e.key === "Escape") {
      // Keep the drawer this might sit in from closing too.
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      // Back to the trigger; the browser then tabs on from there.
      close();
    } else if (printable(e)) {
      const i = seek(e.key);
      if (i >= 0) setActive(i);
    }
  };

  // The list takes focus once it is shown: a popover is hidden until it has
  // been placed, and a hidden element can't be focused.
  const shown = open && (asSheet || !!pos);
  useEffect(() => {
    if (shown) list.current?.focus({ preventScroll: true });
  }, [shown]);

  useEffect(() => {
    if (open && active >= 0) document.getElementById(`${uid}o${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active, uid]);

  // Popover: pinned under the trigger (or above it when there's no room),
  // following it while the page or a drawer scrolls.
  useLayoutEffect(() => {
    if (!open || asSheet) return undefined;
    let raf = 0;
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      const el = list.current;
      if (!r || !el) return;
      const vh = window.innerHeight;
      const vw = document.documentElement.clientWidth;
      const want = Math.min(el.scrollHeight + 2, 340);
      const below = vh - r.bottom - 16;
      const above = r.top - 16;
      const up = below < want && above > below;
      const minWidth = Math.max(r.width, variant === "inline" ? 220 : 0);
      const width = Math.min(Math.max(el.offsetWidth, minWidth), vw - 16);
      const left = Math.min(Math.max(8, align === "end" ? r.right - width : r.left), vw - width - 8);
      setPos({ up, left, minWidth: Math.min(minWidth, vw - 16), maxHeight: Math.max(140, Math.min(340, up ? above : below)), top: up ? undefined : r.bottom + 6, bottom: up ? vh - r.top + 6 : undefined });
    };
    place();
    const onMove = (e) => {
      if (e?.target === list.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(place);
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, asSheet, variant, align]);

  // A press anywhere else closes the popover (the sheet has its own backdrop).
  useEffect(() => {
    if (!open || asSheet) return undefined;
    const onDown = (e) => {
      if (btn.current?.contains(e.target) || pop.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open, asSheet]);

  const onListBlur = (e) => {
    const to = e.relatedTarget;
    if (to && (pop.current?.contains(to) || to === btn.current)) return;
    if (to) setOpen(false);
  };

  const listbox = (
    <ul
      ref={list}
      id={listId}
      role="listbox"
      tabIndex={-1}
      className="sh-sel-list"
      aria-labelledby={labelId || btnId}
      aria-activedescendant={active >= 0 ? optId(active) : undefined}
      onKeyDown={onListKey}
      onBlur={onListBlur}
      style={asSheet || !pos ? undefined : { maxHeight: pos.maxHeight }}
    >
      {options.map((o, i) => (
        <li
          key={String(o.value)}
          id={optId(i)}
          role="option"
          aria-selected={o.value === value}
          aria-disabled={o.disabled || undefined}
          className={cx("sh-sel-opt", i === active && "is-active", o.value === value && "on", o.disabled && "is-disabled")}
          onPointerMove={() => i !== active && usable(i) && setActive(i)}
          onClick={() => pick(i)}
        >
          {o.prefix != null && (
            <span className="sh-sel-prefix" aria-hidden="true">
              {o.prefix}
            </span>
          )}
          <span className="sh-sel-label">{o.label}</span>
          {o.hint && <span className="sh-sel-hint">{o.hint}</span>}
          <Icon name="check" size={16} className="sh-sel-tick" />
        </li>
      ))}
    </ul>
  );

  return (
    <div className={cx("sh-sel", `sh-sel-${variant}`, open && "is-open", disabled && "is-disabled", className)}>
      {label && !labelledBy && (
        <span id={labelId} className="sh-sr">
          {label}
        </span>
      )}
      <button
        ref={btn}
        id={btnId}
        type="button"
        className="sh-sel-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={labelId ? `${labelId} ${btnId}` : undefined}
        disabled={disabled}
        onClick={() => (open ? close() : show())}
        onKeyDown={onTriggerKey}
      >
        {lead && (
          <span className="sh-sel-lead" aria-hidden="true">
            {lead}
          </span>
        )}
        {sel?.prefix != null && (
          <span className="sh-sel-prefix" aria-hidden="true">
            {sel.prefix}
          </span>
        )}
        <span className={cx("sh-sel-value", !sel && "is-empty")}>{sel ? sel.label : placeholder}</span>
        <Icon name="down" size={14} className="sh-sel-chev" />
      </button>
      {open &&
        createPortal(
          asSheet ? (
            <div className="sh-ctl-layer sh-sel-sheet-root" onClick={(e) => e.target === e.currentTarget && close()}>
              <div className="sh-sel-sheet" ref={pop} onKeyDown={(e) => e.key === "Escape" && close()}>
                <div className="sh-sel-sheet-head">
                  <span>{label || placeholder}</span>
                  <button type="button" className="sh-iconbtn" aria-label="Close" onClick={() => close()}>
                    <Icon name="close" />
                  </button>
                </div>
                {listbox}
              </div>
            </div>
          ) : (
            <div
              ref={pop}
              className={cx("sh-ctl-layer", "sh-sel-pop", `sh-sel-pop-${variant}`, pos?.up && "up")}
              style={pos ? { left: pos.left, top: pos.top, bottom: pos.bottom, minWidth: pos.minWidth } : { visibility: "hidden", left: 0, top: 0 }}
            >
              {listbox}
            </div>
          ),
          document.body
        )}
    </div>
  );
}

// ---- range ------------------------------------------------------------------

// Two-thumb slider. `value` is [low, high]; onInput reports while dragging,
// onChange once the thumb is let go (or on each key press). `bars` is an
// optional distribution ([{ from, to, count }] in the slider's units) drawn
// above the track and lit where it falls inside the picked range.
export function RangeSlider({ min, max, step = 1, value, onChange, onInput, format = String, labels = ["Minimum", "Maximum"], captions = ["From", "To"], bars, className }) {
  const track = useRef(null);
  const thumbs = useRef([]);
  const drag = useRef(null);
  const [live, setLive] = useState(null);
  const settle = useRef(0);
  const span = max - min || 1;
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const snap = (v) => clamp(Math.round((v - min) / step) * step + min);
  const start = [clamp(value[0]), clamp(Math.max(value[0], value[1]))];
  const [lo, hi] = live || start;
  const pct = (v) => ((v - min) / span) * 100;
  const peak = Math.max(0, ...(bars || []).map((b) => b.count));

  // A change is shown at once and handed up; the parent (a URL, say) may take
  // a moment to give it back, and quick key repeats must build on the last one.
  const v0 = value[0];
  const v1 = value[1];
  useEffect(() => {
    clearTimeout(settle.current);
    if (!drag.current) setLive(null);
  }, [v0, v1]);
  useEffect(() => () => clearTimeout(settle.current), []);
  const commit = (next) => {
    clearTimeout(settle.current);
    setLive(next);
    settle.current = setTimeout(() => setLive(null), 900);
    onChange(next);
  };

  const at = (x) => {
    const r = track.current.getBoundingClientRect();
    return snap(min + ((x - r.left) / (r.width || 1)) * span);
  };
  const follow = (x) => {
    const d = drag.current;
    const v = at(x);
    const [a, b] = d.now;
    // Both thumbs on one spot: the first move decides which one comes along.
    if (d.which == null) {
      if (v === a) return;
      d.which = v < a ? 0 : 1;
      thumbs.current[d.which]?.focus({ preventScroll: true });
    }
    const next = d.which === 0 ? [Math.min(v, b), b] : [a, Math.max(v, a)];
    if (next[0] === a && next[1] === b) return;
    d.now = next;
    setLive(next);
    onInput?.(next);
  };

  const onPointerDown = (e) => {
    if (e.button !== 0 || !track.current) return;
    e.preventDefault();
    const v = at(e.clientX);
    const dl = Math.abs(v - lo);
    const dh = Math.abs(v - hi);
    const which = lo === hi && v === lo ? null : dl < dh || (dl === dh && v < lo) ? 0 : 1;
    drag.current = { which, now: [lo, hi], from: [lo, hi], id: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    thumbs.current[which ?? 1]?.focus({ preventScroll: true });
    if (which != null) follow(e.clientX);
  };
  const onPointerMove = (e) => {
    if (drag.current?.id === e.pointerId) follow(e.clientX);
  };
  const onPointerUp = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (d.now[0] !== d.from[0] || d.now[1] !== d.from[1]) commit(d.now);
    else setLive(null);
  };

  const onKey = (which) => (e) => {
    const big = Math.max(step, Math.round(span / 10 / step) * step);
    const cur = which ? hi : lo;
    const floor = which ? lo : min;
    const ceil = which ? max : hi;
    const target = { ArrowLeft: cur - step, ArrowDown: cur - step, ArrowRight: cur + step, ArrowUp: cur + step, PageDown: cur - big, PageUp: cur + big, Home: floor, End: ceil }[e.key];
    if (target == null) return;
    e.preventDefault();
    const v = Math.min(ceil, Math.max(floor, target === floor || target === ceil ? target : snap(target)));
    if (v !== cur) commit(which ? [lo, v] : [v, hi]);
  };

  return (
    <div className={cx("sh-range", live && "is-dragging", className)}>
      {peak > 0 && (
        <div className="sh-range-bars" aria-hidden="true">
          {bars.map((b, i) => (
            <i
              key={i}
              className={b.count && b.to >= lo && b.from <= hi ? "on" : undefined}
              style={{ left: `${pct(b.from)}%`, width: `${pct(b.to) - pct(b.from)}%`, height: b.count ? `max(2px, ${(b.count / peak) * 100}%)` : 0 }}
            />
          ))}
        </div>
      )}
      <div className="sh-range-zone" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <div className="sh-range-track" ref={track}>
          <i className="sh-range-fill" style={{ left: `${pct(lo)}%`, width: `${pct(hi) - pct(lo)}%` }} />
          {[lo, hi].map((v, i) => (
            <span
              key={i}
              ref={(el) => {
                thumbs.current[i] = el;
              }}
              role="slider"
              tabIndex={0}
              className="sh-range-thumb"
              style={{ left: `${pct(v)}%` }}
              aria-label={labels[i]}
              aria-orientation="horizontal"
              aria-valuemin={i ? lo : min}
              aria-valuemax={i ? max : hi}
              aria-valuenow={v}
              aria-valuetext={format(v)}
              onKeyDown={onKey(i)}
            />
          ))}
        </div>
      </div>
      <div className="sh-range-out">
        <span>
          <small>{captions[0]}</small>
          {format(lo)}
        </span>
        <i aria-hidden="true" />
        <span>
          <small>{captions[1]}</small>
          {format(hi)}
        </span>
      </div>
    </div>
  );
}
